/**
 * Pure display helpers for travel segments, shared by the itinerary screen's
 * `TravelSegment` row and the trip-overview itinerary tile's compact segment
 * row — one formatting source so "12 Mins · 0.4 Mi" reads identically on both.
 * Structural param types keep the overview core free of api-types coupling.
 */

import { pluralize } from '@/utils/plural'

/** The route fields the display needs (either the walking or transit route). */
export interface SegmentRouteDisplay {
	durationSeconds: number | null
	distanceMeters: number | null
	transportMode: string
}

/** Minimum segment shape: the two cached routes a segment may carry. */
export interface SegmentRoutesInput {
	walkingRoute?: SegmentRouteDisplay | null
	transitRoute?: SegmentRouteDisplay | null
}

export const formatSegmentDuration = (seconds: number | null): string => {
	if (seconds === null) return 'N/A'
	const minutes = Math.round(seconds / 60)
	if (minutes <= 90) return `${minutes} Mins`
	const hours = Math.floor(minutes / 60)
	const remMinutes = minutes % 60
	const hourLabel = pluralize(hours, 'Hour')
	if (remMinutes === 0) return `${hours} ${hourLabel}`
	return `${hours} hrs ${remMinutes} min`
}

export const formatSegmentDistance = (meters: number | null): string => {
	if (meters === null) return 'N/A'
	const miles = meters / 1609.344
	return `${miles.toFixed(1)} Mi`
}

const hasRouteData = (route: SegmentRouteDisplay | null | undefined): boolean =>
	route != null && (route.durationSeconds !== null || route.distanceMeters !== null)

/**
 * The route a segment should display: transit when it has data, else walking,
 * else whichever exists (so a data-less segment still shows "ETA N/A").
 */
export const pickSegmentRoute = <T extends SegmentRoutesInput>(
	segment: T,
): NonNullable<T['transitRoute'] | T['walkingRoute']> | null => {
	if (hasRouteData(segment.transitRoute)) return segment.transitRoute!
	if (hasRouteData(segment.walkingRoute)) return segment.walkingRoute!
	return segment.transitRoute ?? segment.walkingRoute ?? null
}

/** One-line summary, e.g. "12 Mins · 0.4 Mi", or "ETA N/A" without route data. */
export const segmentRouteLabel = (route: SegmentRouteDisplay): string => {
	if (route.durationSeconds === null && route.distanceMeters === null) return 'ETA N/A'
	return `${formatSegmentDuration(route.durationSeconds)} · ${formatSegmentDistance(route.distanceMeters)}`
}
